import { NavigateFunction } from "react-router-dom";
import { getUserTokenOrRedirect } from "../../../shared/services/auth.service";
import { CreateCompanyCrewMemberDto, CompanyCrewMemberDto, CrewCandidateDto, UpdateCompanyCrewMemberDto } from "../dtos/company-crew-member.dto";
import { JobCrewMemberDto, UpdateJobCrewMembersDto } from "../dtos/job-crew-member.dto";
import {crewTimePartialMessage} from "../../work-order/work-order.service";

export async function getAllCrewMembers(navigate: NavigateFunction): Promise<CompanyCrewMemberDto[]> {
  const token = await getUserTokenOrRedirect(navigate);
  const crewMembers = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/crew-members`,
    { headers: { Authorization: `Bearer ${token}` } }
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to fetch crew members: ${response.statusText}`);
    }
    return await response.json() as CompanyCrewMemberDto[];
  });
  return crewMembers;
}

/** SQ-522: who a job's crew is picked from — team members with their billable rates. */
export async function getCrewCandidates(navigate: NavigateFunction): Promise<CrewCandidateDto[]> {
  const token = await getUserTokenOrRedirect(navigate);
  return await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/crew-candidates`,
    { headers: { Authorization: `Bearer ${token}` } }
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to fetch crew candidates: ${response.statusText}`);
    }
    return await response.json() as CrewCandidateDto[];
  });
}

export async function getCrewMember(navigate: NavigateFunction, crewMemberId: number): Promise<CompanyCrewMemberDto> {
  const token = await getUserTokenOrRedirect(navigate);
  const crewMember = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/crew-members/${crewMemberId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to fetch crew member: ${response.statusText}`);
    }
    return await response.json() as CompanyCrewMemberDto;
  });
  return crewMember;
}

export async function createCrewMember(navigate: NavigateFunction, crewMember: CreateCompanyCrewMemberDto): Promise<CompanyCrewMemberDto> {
  const token = await getUserTokenOrRedirect(navigate);
  const createdCrewMember = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/crew-members`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(crewMember)
    }
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to create crew member: ${response.statusText}`);
    }
    return await response.json() as CompanyCrewMemberDto;
  });
  return createdCrewMember;
}

export async function updateCrewMember(navigate: NavigateFunction, crewMemberId: number, crewMember: UpdateCompanyCrewMemberDto): Promise<CompanyCrewMemberDto> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/crew-members/${crewMemberId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(crewMember),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  ).then(async r => r.ok ? (await r.json()) as CompanyCrewMemberDto : Promise.reject(r));

  return response;
}

export async function deleteCrewMember(navigate: NavigateFunction, crewMemberId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/crew-members/${crewMemberId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to delete crew member: ${response.statusText}`);
  }
}

// Job Crew Member Services
export async function getJobCrewMembers(navigate: NavigateFunction, jobId: number): Promise<JobCrewMemberDto[]> {
  const token = await getUserTokenOrRedirect(navigate);
  const crewMembers = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/jobs/${jobId}/crew-members`,
    { headers: { Authorization: `Bearer ${token}` } }
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to fetch job crew members: ${response.statusText}`);
    }
    return await response.json() as JobCrewMemberDto[];
  });
  return crewMembers;
}

export async function updateJobCrewMembers(navigate: NavigateFunction, jobId: number,  updateDto: UpdateJobCrewMembersDto): Promise<JobCrewMemberDto[]> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/jobs/${jobId}/crew-members`,
    {
      method: 'PATCH',
      body: JSON.stringify(updateDto),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  ).then(async r => {
    if (r.ok) return (await r.json()) as JobCrewMemberDto[];
    const crewTime = await crewTimePartialMessage(r);
    return Promise.reject(crewTime ? new Error(crewTime) : r);
  });

  return response;
}

/**
 * SQ-559: starts or stops a crew member's job clock, which runs on the
 * server (every phone and browser sees it; nothing the page does stops it).
 * Answers the job's crew. [backDatedSeconds] dates a start back that far
 * (a run this browser kept before clocks moved to the server).
 */
export async function setJobCrewClock(navigate: NavigateFunction, jobId: number, companyCrewMemberId: number, start: boolean, backDatedSeconds = 0): Promise<JobCrewMemberDto[]> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/jobs/${jobId}/crew-members/${companyCrewMemberId}/clock/${start ? 'start' : 'stop'}`,
    {
      method: 'POST',
      body: JSON.stringify(start ? {alreadyRunningSeconds: backDatedSeconds} : {secondsAgo: 0}),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => undefined) as {message?: unknown} | undefined;
    throw new Error(typeof body?.message === 'string' && body.message
      ? body.message
      : `The clock could not be ${start ? 'started' : 'stopped'}.`);
  }
  return await response.json() as JobCrewMemberDto[];
}
